import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import { API_URL } from '../config/api';
import './QuestionPackages.css';

const QuestionPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  // GST calculation helper
  const GST_RATE = 0.18; // 18%
  const calculateGST = (price) => {
    return parseFloat((price * GST_RATE).toFixed(2));
  };
  const calculateTotal = (price) => {
    return parseFloat((price + calculateGST(price)).toFixed(2));
  };
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const { isAuthenticated, isAdmin: checkIsAdmin, user } = useAuth();
  const isAdmin = checkIsAdmin() || (user && user.role === 'ADMIN');

  useEffect(() => {
    fetchPackages();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showPurchaseModal || showLoginModal || showAuthModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [showPurchaseModal, showLoginModal, showAuthModal]);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authorization header if user is logged in
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/question-packages`, {
        credentials: 'include',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setPackages(data);
      } else {
        console.error('API returned non-array data:', data);
        setPackages([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setPackages([]);
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'BEGINNER': 'green',
      'INTERMEDIATE': 'blue',
      'ADVANCED': 'orange',
      'EXPERT': 'red'
    };
    return colors[difficulty] || 'gray';
  };

  const getDifficultyBadge = (difficulty) => {
    const color = getDifficultyColor(difficulty);
    return (
      <span className={`difficulty-badge ${color}`}>
        {difficulty}
      </span>
    );
  };

  const handlePurchaseClick = (pkg) => {
    if (!isAuthenticated()) {
      setShowLoginModal(true);
      return;
    }
    setSelectedPackage(pkg);
    setShowPurchaseModal(true);
  };

  const handlePurchase = async () => {
    try {
      setShowPurchaseModal(false);
      
      // Check if user is admin - admins get free access
      if (isAdmin) {
        // Direct enrollment for admin users
        try {
          const enrollResponse = await fetch(`${API_URL}/api/question-packages/${selectedPackage.id}/purchase`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });
          
          if (enrollResponse.ok) {
            setPaymentDetails({
              packageName: selectedPackage.name,
              transactionId: 'ADMIN_FREE_ACCESS',
              orderId: 'ADMIN_' + Date.now(),
              amount: 0
            });
            setShowPurchaseModal(false); // Close purchase modal
            setShowPaymentSuccess(true);
            fetchPackages();
            return;
          }
        } catch (error) {
          console.error('Admin enrollment error:', error);
        }
      }
      
      // Step 1: Create Razorpay order for regular users
      const orderResponse = await fetch(`${API_URL}/api/question-packages/${selectedPackage.id}/payment/create-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        alert(errorData.error || 'Failed to create order. Please try again.');
        return;
      }
      
      const orderData = await orderResponse.json();
      
      // Step 2: Open Razorpay checkout
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'YCode Universe',
        description: orderData.packageName,
        order_id: orderData.orderId,
        prefill: {
          name: localStorage.getItem('username') || '',
          email: localStorage.getItem('email') || '',
        },
        theme: {
          color: '#8b5cf6'
        },
        handler: async function (response) {
          // Step 3: Verify payment on backend
          try {
            const verifyResponse = await fetch(`${API_URL}/api/question-packages/${selectedPackage.id}/payment/verify`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify({
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature
              })
            });
            
            if (verifyResponse.ok) {
              // Show beautiful success modal
              setPaymentDetails({
                packageName: selectedPackage.name,
                transactionId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                amount: calculateTotal(selectedPackage.price)
              });
              setShowPurchaseModal(false); // Close purchase modal
              setShowPaymentSuccess(true);
              
              // Refresh packages to update purchase status
              fetchPackages();
            } else {
              const errorData = await verifyResponse.json();
              alert(`Payment verification failed: ${errorData.error}\nPlease contact support with Order ID: ${response.razorpay_order_id}`);
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment completed but verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: function() {
            alert('Payment cancelled. You can try again anytime.');
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to initiate payment. Please try again.');
    }
  };

  const filteredPackages = packages.filter(pkg => {
    const categoryMatch = selectedCategory === 'all' || pkg.category === selectedCategory;
    const difficultyMatch = selectedDifficulty === 'all' || pkg.difficultyLevel === selectedDifficulty;
    return categoryMatch && difficultyMatch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center pt-20">
        <div className="text-white text-xl">Loading question packages...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pt-20">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-900/30 via-pink-900/30 to-purple-900/30 border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                  Question Packages
                </span>
              </h1>
              <p className="text-gray-300 text-sm sm:text-base">
                Master programming concepts with comprehensive practice questions
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
                <div className="text-xs text-gray-400 mb-1">Available</div>
                <div className="text-lg font-bold text-purple-300">
                  {filteredPackages.length} {filteredPackages.length === 1 ? 'Package' : 'Packages'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap gap-4 justify-center">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-white/10 backdrop-blur-md text-white border border-white/20 rounded-lg px-4 py-2"
          >
            <option value="all">All Categories</option>
            <option value="Programming">Programming</option>
            <option value="Data Structures">Data Structures</option>
            <option value="Algorithms">Algorithms</option>
            <option value="Web Development">Web Development</option>
            <option value="Database">Database</option>
            <option value="System Design">System Design</option>
          </select>

          <select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
            className="bg-white/10 backdrop-blur-md text-white border border-white/20 rounded-lg px-4 py-2"
          >
            <option value="all">All Difficulties</option>
            <option value="BEGINNER">Beginner</option>
            <option value="INTERMEDIATE">Intermediate</option>
            <option value="ADVANCED">Advanced</option>
            <option value="EXPERT">Expert</option>
          </select>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
        {filteredPackages.length === 0 ? (
          <div className="text-center">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-semibold text-white mb-4">No Packages Available</h3>
            <p className="text-gray-300">Check back later for new question packages!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPackages.map((pkg) => (
              <div key={pkg.id} className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 hover:border-purple-400/50 transition-all duration-300 hover:transform hover:scale-105 relative">
                {/* Discount Badge */}
                {pkg.isOnSale && (
                  <div className="absolute top-4 right-4 z-10">
                    {pkg.discountPercentage ? (
                      <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg animate-pulse">
                        {pkg.discountPercentage}% OFF
                      </span>
                    ) : (
                      <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
                        ON SALE
                      </span>
                    )}
                  </div>
                )}
                
                <div className="text-4xl mb-4">📦</div>
                
                <div className="flex items-center justify-between mb-4">
                  {getDifficultyBadge(pkg.difficultyLevel)}
                  <span className="text-gray-400 text-sm">
                    {pkg.estimatedDurationHours}h
                  </span>
                </div>

                <h3 className="text-xl font-semibold text-white mb-4">
                  {pkg.name}
                </h3>

                <p className="text-gray-300 mb-6 text-sm">
                  {pkg.description}
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Questions:</span>
                    <span className="text-white">{pkg.totalQuestions || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Category:</span>
                    <span className="text-purple-400">{pkg.category}</span>
                  </div>
                  <div className="border-t border-white/10 pt-3 mt-2">
                    {pkg.isOnSale && pkg.originalPrice ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-xs">Original Price:</span>
                          <span className="text-gray-500 line-through text-sm">₹{pkg.originalPrice}</span>
                        </div>
                        {pkg.discountPercentage && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-xs">Discount:</span>
                            <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-2 py-1 rounded text-xs font-bold">
                              {pkg.discountPercentage}% OFF
                            </span>
                          </div>
                        )}
                        <div className="space-y-1 pt-2 border-t border-white/10">
                          <div className="flex items-center justify-between">
                            <span className="text-white font-semibold text-sm">Price:</span>
                            <span className="text-white font-semibold text-sm">₹{pkg.price}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-xs">GST (18%):</span>
                            <span className="text-gray-400 text-xs">₹{calculateGST(pkg.price)}</span>
                          </div>
                          <div className="flex items-center justify-between pt-1 border-t border-white/10">
                            <span className="text-white font-bold text-sm">Total:</span>
                            <span className="text-green-400 font-bold text-xl">₹{calculateTotal(pkg.price)}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-semibold text-sm">Price:</span>
                          <span className="text-white font-semibold text-sm">₹{pkg.price}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-xs">GST (18%):</span>
                          <span className="text-gray-400 text-xs">₹{calculateGST(pkg.price)}</span>
                        </div>
                        <div className="flex items-center justify-between pt-1 border-t border-white/10">
                          <span className="text-white font-bold text-sm">Total:</span>
                          <span className="text-green-400 font-bold text-xl">₹{calculateTotal(pkg.price)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Link
                    to={`/question-packages/${pkg.id}`}
                    className={`${pkg.isPurchased ? 'w-full' : 'flex-1'} bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition duration-300 text-center shadow-lg hover:shadow-purple-500/50`}
                  >
                    {pkg.isPurchased ? '✅ Access Package' : '📖 View Details'}
                  </Link>
                  {!pkg.isPurchased && (
                    <button 
                      onClick={() => handlePurchaseClick(pkg)}
                      className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition duration-300 shadow-lg hover:shadow-green-500/50"
                    >
                      🛒 Buy Now
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 backdrop-blur-md rounded-lg p-12 text-center border border-purple-400/30">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Master Programming?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Choose from our comprehensive question packages and start your learning journey today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/ide"
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 transform hover:scale-105 shadow-lg hover:shadow-purple-500/50"
            >
              💻 Try Our IDE
            </Link>
            <Link
              to="/courses"
              className="bg-transparent border-2 border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 transform hover:scale-105"
            >
              📚 Explore Courses
            </Link>
          </div>
        </div>
      </div>

      {/* Login Required Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4" onClick={() => setShowLoginModal(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border-2 border-orange-400/50 shadow-2xl shadow-orange-500/30" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🔒</div>
              <h3 className="text-2xl font-bold text-white mb-2">Login Required</h3>
              <p className="text-gray-300 text-sm mb-4">
                You need to be logged in to purchase packages. Please login or create an account to continue.
              </p>
            </div>

            <div className="bg-orange-900/30 border border-orange-400/30 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <span className="text-2xl">ℹ️</span>
                <div>
                  <p className="text-orange-300 text-xs font-bold mb-1">Why Login?</p>
                  <p className="text-gray-300 text-xs">Logging in allows us to track your progress, save your work, and unlock all questions in your purchased packages.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  setShowAuthModal(true);
                }}
                className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-orange-500/50 transform hover:scale-105"
              >
                🚀 Go to Login
              </button>
              <button
                onClick={() => setShowLoginModal(false)}
                className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold text-sm transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && selectedPackage && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4" onClick={() => setShowPurchaseModal(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border-2 border-purple-400/50 shadow-2xl shadow-purple-500/30 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🛒</div>
              <h3 className="text-2xl font-bold text-white mb-1">Purchase Package</h3>
              <p className="text-gray-300 text-sm">Complete your purchase to get started</p>
            </div>

            <div className="bg-white/10 rounded-xl p-4 mb-4 border border-white/20">
              <h4 className="text-lg font-bold text-white mb-3">{selectedPackage.name}</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Category:</span>
                  <span className="text-purple-400 font-semibold">{selectedPackage.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Difficulty:</span>
                  <span className="text-white">{getDifficultyBadge(selectedPackage.difficultyLevel)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Questions:</span>
                  <span className="text-white font-semibold">{selectedPackage.totalQuestions || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Duration:</span>
                  <span className="text-white font-semibold">{selectedPackage.estimatedDurationHours}h</span>
                </div>
                <div className="border-t border-white/20 pt-4 mt-3">
                  {selectedPackage.isOnSale && selectedPackage.originalPrice ? (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Original Price:</span>
                        <span className="text-gray-400 text-base line-through">₹{selectedPackage.originalPrice}</span>
                      </div>
                      {selectedPackage.discountPercentage && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">Discount:</span>
                          <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-lg text-sm font-bold shadow-md">
                            {selectedPackage.discountPercentage}% OFF
                          </span>
                        </div>
                      )}
                      <div className="space-y-2 pt-3 border-t-2 border-white/20">
                        <div className="flex justify-between items-center">
                          <span className="text-white font-semibold text-sm">Price:</span>
                          <span className="text-white font-semibold text-sm">₹{selectedPackage.price}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">GST (18%):</span>
                          <span className="text-gray-400 text-sm">₹{calculateGST(selectedPackage.price)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-white/20">
                          <span className="text-white font-bold text-lg">Total Price:</span>
                          <span className="text-green-400 font-bold text-3xl">₹{calculateTotal(selectedPackage.price)}</span>
                        </div>
                      </div>
                      {selectedPackage.offerDescription && (
                        <div className="bg-gradient-to-r from-orange-900/40 to-red-900/40 border border-orange-500/50 rounded-lg p-3 mt-3">
                          <p className="text-orange-300 text-sm text-center font-medium">⚡ {selectedPackage.offerDescription}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-semibold text-sm">Price:</span>
                        <span className="text-white font-semibold text-sm">₹{selectedPackage.price}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">GST (18%):</span>
                        <span className="text-gray-400 text-sm">₹{calculateGST(selectedPackage.price)}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-white/20">
                        <span className="text-white font-bold text-lg">Total Price:</span>
                        <span className="text-green-400 font-bold text-3xl">₹{calculateTotal(selectedPackage.price)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handlePurchase}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-green-500/50 transform hover:scale-105"
              >
                ✅ Proceed to Payment
              </button>
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold text-sm transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Success Modal */}
      {showPaymentSuccess && paymentDetails && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 10000 }}
          onClick={() => setShowPaymentSuccess(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            {/* Success Icon with Animation */}
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 p-8 rounded-t-2xl text-center relative overflow-hidden">
              {/* Animated Background Circles */}
              <div className="absolute top-0 left-0 w-full h-full">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-white/10 rounded-full animate-ping"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-white/20 rounded-full"></div>
              </div>
              
              {/* Success Icon */}
              <div className="relative z-10 mb-4">
                <div className="w-20 h-20 bg-white rounded-full mx-auto flex items-center justify-center shadow-lg">
                  <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              
              <h2 className="relative z-10 text-3xl font-bold text-white mb-2">
                🎉 Payment Successful!
              </h2>
              <p className="relative z-10 text-green-100 text-sm">
                Your purchase has been completed
              </p>
            </div>

            {/* Payment Details */}
            <div className="p-6">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 mb-5 space-y-3">
                <div className="flex justify-between items-start py-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium text-sm">Package</span>
                  <span className="text-gray-900 font-bold text-sm text-right max-w-[60%]">
                    {paymentDetails.packageName}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium text-sm">Amount Paid</span>
                  <span className="text-green-600 font-bold text-base">
                    ₹{paymentDetails.amount}
                  </span>
                </div>
                
                <div className="flex justify-between items-start py-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium text-sm">Transaction ID</span>
                  <span className="text-gray-900 font-mono text-xs text-right break-all max-w-[60%]">
                    {paymentDetails.transactionId}
                  </span>
                </div>
                
                <div className="flex justify-between items-start py-2">
                  <span className="text-gray-600 font-medium text-sm">Order ID</span>
                  <span className="text-gray-900 font-mono text-xs text-right break-all max-w-[60%]">
                    {paymentDetails.orderId}
                  </span>
                </div>
              </div>

              {/* Success Message */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-5">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="text-green-900 font-semibold text-sm mb-1">
                      You can now start practicing!
                    </p>
                    <p className="text-green-700 text-xs">
                      Go to your dashboard to access all questions in this package.
                    </p>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowPaymentSuccess(false)}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-green-500/50 transform hover:scale-105"
              >
                Awesome! 🎉
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </div>
  );
};

export default QuestionPackages; 
